import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.exploreandwin.com/api';
const STORAGE_KEY = 'cms_language';
const DEFAULT_LANGUAGE = 'en';

// Available languages - will be updated from API
const FALLBACK_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', isDefault: true },
  { code: 'de', name: 'German', nativeName: 'Deutsch', isDefault: false }
];

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Initialize from localStorage or default
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANGUAGE;
  });
  const [translations, setTranslations] = useState({});
  const [availableLanguages, setAvailableLanguages] = useState(FALLBACK_LANGUAGES);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available languages
  const fetchLanguages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/cms/translations`);
      if (response.data.languages && response.data.languages.length > 0) {
        setAvailableLanguages(response.data.languages);
      }
    } catch (err) {
      console.warn('Failed to fetch CMS languages, using fallback:', err.message);
      // Keep using fallback languages
    }
  }, []);

  // Fetch translations for current language
  const fetchTranslations = useCallback(async (lang) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_URL}/cms/translations/${lang}`);
      setTranslations(response.data.translations || {});
    } catch (err) {
      console.error('Failed to fetch translations:', err);
      setError('Failed to load translations');
      // Keep existing translations on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    fetchLanguages();
    fetchTranslations(currentLanguage);
  }, [fetchLanguages, fetchTranslations, currentLanguage]);

  // Change language
  const changeLanguage = useCallback(async (newLang) => {
    if (newLang === currentLanguage) return;

    // Update state and storage
    setCurrentLanguage(newLang);
    localStorage.setItem(STORAGE_KEY, newLang);

    // Fetch new translations
    await fetchTranslations(newLang);

    // Try to update user preference on server (if authenticated)
    const token = localStorage.getItem('cms_accessToken');
    if (token) {
      try {
        await axios.put(
          `${API_URL}/cms/translations/user-preference`,
          { language: newLang },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        // Non-critical, preference will be stored locally
        console.warn('Failed to save language preference to server:', err.message);
      }
    }
  }, [currentLanguage, fetchTranslations]);

  // Translation function with fallback
  const t = useCallback((key, params = {}) => {
    let value = translations[key];

    // If no translation found, return key (fallback)
    if (value === undefined || value === '') {
      // For development, return key to make missing translations visible
      return key;
    }

    // Replace parameters like {name}, {count}, etc.
    if (params && typeof params === 'object') {
      Object.keys(params).forEach(param => {
        value = value.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
      });
    }

    return value;
  }, [translations]);

  // Sync language with user preference on login
  const syncWithUserPreference = useCallback(async () => {
    const token = localStorage.getItem('cms_accessToken');
    if (!token) return;

    try {
      const response = await axios.get(`${API_URL}/cms/translations/user-preference`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const serverLang = response.data.language;
      if (serverLang && serverLang !== currentLanguage) {
        // Server preference takes precedence
        setCurrentLanguage(serverLang);
        localStorage.setItem(STORAGE_KEY, serverLang);
        await fetchTranslations(serverLang);
      }
    } catch (err) {
      // Non-critical, use local preference
      console.warn('Failed to sync language preference:', err.message);
    }
  }, [currentLanguage, fetchTranslations]);

  // Get language info
  const getCurrentLanguageInfo = useCallback(() => {
    return availableLanguages.find(l => l.code === currentLanguage) || availableLanguages[0];
  }, [availableLanguages, currentLanguage]);

  // Get content language for API calls (challenges, events, etc.)
  const getContentLanguage = useCallback(() => {
    return currentLanguage;
  }, [currentLanguage]);

  const value = {
    // State
    currentLanguage,
    translations,
    availableLanguages,
    isLoading,
    error,

    // Functions
    t,
    changeLanguage,
    syncWithUserPreference,
    getCurrentLanguageInfo,
    getContentLanguage,
    refreshTranslations: () => fetchTranslations(currentLanguage)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
